import { REPEAT_NOT_FULFILLED_ERROR_MESSAGE } from '#api/errors'
import {
  type CheckoutQuery,
  CheckoutState,
  type PayWithTopUp,
  type ScratchcardProduct,
  type StoredPaymentInstrument,
  SubscriptionType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { useProductStore } from '#product/app/stores/product'

export enum ProductType {
  HighFrequencyGame = 'HighFrequencyGame',
  Lottery = 'Lottery',
  LotterySubscription = 'LotterySubscription',
  Scratchcard = 'Scratchcard',
}

type UseCheckoutOptions = {
  /**
   * When `false`, the checkout query is not launched until `start()` is called.
   * Defaults to `true` (query fires as soon as a product is available).
   */
  immediate?: boolean
}

const CHECKOUT_STATE_KEY = 'checkoutEnabled'
const BALANCE_STATE_KEY = 'balance'
const TOP_UP_PAYMENT_STATE_KEY = 'currentTopUpPayment'
const STORED_PAYMENT_STATE_KEY = 'payment'

export const useCheckout = (productName?: string, options?: UseCheckoutOptions) => {
  const canUseBalance = useState<boolean>(BALANCE_STATE_KEY, () => false)
  const currentTopUpPayment = useState<Maybe<StoredPaymentInstrument>>(
    TOP_UP_PAYMENT_STATE_KEY,
    () => null,
  )
  const checkoutQuery = useState<ReturnType<typeof useCheckoutQuery> | undefined>()

  const checkoutEnabled = useState<boolean>(CHECKOUT_STATE_KEY, () => options?.immediate !== false)

  const { navigateToLocalized } = useRouterUtils()
  const { getProductFromRoute } = useProductGetter()

  const route = useRoute()

  const { deleteProductId, getProduct } = useProductStore()
  const product = computed(() => productName ?? getProductFromRoute(route))

  if (!product?.value)
    throw new Error(
      'useCheckout composable can only be used in valid lottery/scratchcard/highfrequency routes or with a valid product name as argument.',
    )

  const isAddFundsEnabled = useIsFeatureFlagEnabled('addFunds')

  const hasProduct = computed(() => !!product.value && !!getProduct(product.value))
  const isQueryEnabled = computed(() => hasProduct.value && checkoutEnabled.value)
  const pending = ref(false)

  if (!checkoutQuery.value) {
    checkoutQuery.value = useCheckoutQuery(
      {
        licensedTerritory: useLicensedTerritory({ raw: true }),
        productId: getProduct(product.value)?.id,
      },
      {
        context: {
          ...setRepeatOptions<CheckoutQuery>({
            delay: 1_000,
            errorOnNotFulfilled: true,
            fixedDelay: true,
            repeatConditions: (response) =>
              response.data?.checkout.state === CheckoutState.Pending ||
              (!!response?.errors && response.errors.some((e) => e.message === 'failure.payment')),
          }),
          ...{
            timeout: 20_000,
          },
        },
        enabled: isQueryEnabled,
        fetchPolicy: 'network-only',
      },
    )
  }

  checkoutQuery.value.onError((error) => {
    switch (error.message) {
      case REPEAT_NOT_FULFILLED_ERROR_MESSAGE:
        pending.value = true
        break
      case 'validation.product.expired':
        if (!product.value) {
          navigateToLocalized('/')
          return
        }
        deleteProductId(product.value)
        navigateToLocalized(`/scratchcard/${product.value}`)
        break
      default:
        navigateToLocalized('/')
    }
  })

  const { log } = useDatadog()

  checkoutQuery.value.onResult(({ data, loading }) => {
    if (loading || !data) return
    canUseBalance.value = !!data.checkout.payment?.payWithBalance
  })

  const checkoutResult = computed(() => toValue(checkoutQuery.value?.result))

  const paymentId = computed(() => {
    const payment = checkoutResult.value?.checkout.payment
    return payment?.__typename === 'PayWithTopUp' ? payment.paymentId : null
  })

  const checkoutLoading = computed(() => toValue(checkoutQuery.value?.loading) ?? true)

  const isPending = computed(() => pending.value)

  const isFundingNeeded = computed(
    () =>
      isAddFundsEnabled && (checkoutResult.value?.checkout.payment?.topUpAmount?.value || 0) > 0,
  )

  const isSubscription = computed(() => {
    if (
      checkoutResult.value?.checkout.product?.__typename !== 'LotteryProduct' ||
      !checkoutResult.value?.checkout.product.subscriptionOptions
    ) {
      return false
    }

    return [SubscriptionType.Prepaid, SubscriptionType.Renewing].includes(
      checkoutResult.value.checkout.product.subscriptionOptions.type,
    )
  })

  const isPrepaidSubscription = computed(() => {
    if (checkoutResult.value?.checkout.product?.__typename !== 'LotteryProduct') return false
    return (
      checkoutResult.value.checkout.product.subscriptionOptions?.type === SubscriptionType.Prepaid
    )
  })

  const prepaidSubscriptionAmount = computed(() => {
    if (checkoutResult.value?.checkout.product?.__typename !== 'LotteryProduct') return
    if (!isPrepaidSubscription.value) return

    return checkoutResult.value?.checkout.product.subscriptionOptions?.drawsPerPurchase
  })

  const priceBreakdown = computed(() => {
    const product = checkoutResult.value?.checkout.product
    switch (product?.__typename) {
      case 'HighFrequencyProduct':
      case 'LotteryProduct':
      case 'ScratchcardProduct':
        return product.priceBreakdown
      default:
        if (!product) {
          log('info', {
            message: `Product in CheckoutQuery not available`,
            messageContext: {
              paymentId: paymentId.value,
              product,
            },
          })
        }

        return null
    }
  })

  const productType = computed(() => {
    switch (checkoutResult.value?.checkout.product?.__typename) {
      case 'HighFrequencyProduct':
        return ProductType.HighFrequencyGame
      case 'LotteryProduct':
        return ProductType.Lottery
      case 'ScratchcardProduct':
        return ProductType.Scratchcard
      default:
        return null
    }
  })

  const scratchcardExpired = computed(() => {
    if (productType.value !== ProductType.Scratchcard) return false
    return new Date() > new Date(scratchcardExpiresAt.value)
  })

  const scratchcardExpiresAt = computed(
    () => (checkoutResult.value?.checkout?.product as ScratchcardProduct)?.expiresAt,
  )

  const setTopUpPayment = (_topUpPayment: StoredPaymentInstrument) => {
    currentTopUpPayment.value = _topUpPayment
  }

  const storedPayments = computed(
    () => (checkoutResult.value?.checkout.payment as PayWithTopUp)?.stored,
  )

  const initialPayments = computed(
    () => (checkoutResult.value?.checkout.payment as PayWithTopUp)?.initial,
  )

  const topUpPayment = computed(() => currentTopUpPayment.value)

  const start = () => {
    checkoutEnabled.value = true
  }

  const reset = () => {
    checkoutQuery.value = undefined
    clearNuxtState([
      CHECKOUT_STATE_KEY,
      BALANCE_STATE_KEY,
      TOP_UP_PAYMENT_STATE_KEY,
      STORED_PAYMENT_STATE_KEY,
    ])
  }

  return {
    canUseBalance,
    checkoutLoading,
    checkoutRefetch: checkoutQuery.value.refetch,
    checkoutResult,
    initialPayments,
    isFundingNeeded,
    isPending,
    isPrepaidSubscription,
    isSubscription,
    paymentId,
    prepaidSubscriptionAmount,
    priceBreakdown,
    productType,
    reset,
    scratchcardExpired,
    scratchcardExpiresAt,
    setTopUpPayment,
    start,
    storedPayments,
    topUpPayment,
  }
}
